import { useEffect, useRef, useState } from "react";
import { Camera, Loader2, Check, ScanLine, X, Zap } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { imgUrl, fmtGram } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { SubcategoryInput } from "@/components/SubcategoryInput";
import { TrayInput, loadTray, saveTray } from "@/components/TrayInput";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const KARATS = ["8", "14", "18", "22", "24"];
const PREFS_KEY = "orse_quickadd_prefs";

const loadPrefs = () => {
  try { return JSON.parse(localStorage.getItem(PREFS_KEY) || "{}"); } catch { return {}; }
};

export default function QuickAdd() {
  const [categories, setCategories] = useState([]);
  const prefs = loadPrefs();

  // single quick add state
  const [karat, setKarat] = useState(prefs.karat || "22");
  const [category, setCategory] = useState(prefs.category || "");
  const [subcategory, setSubcategory] = useState("");
  const [tray, setTray] = useState(loadTray());
  const [group, setGroup] = useState("");
  const [milyem, setMilyem] = useState(prefs.milyem || "");
  const [gram, setGram] = useState("");
  const [name, setName] = useState("");
  const [barcode, setBarcode] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [session, setSession] = useState([]);

  const photoRef = useRef();
  const gramRef = useRef();

  // scanner
  const [scanOpen, setScanOpen] = useState(false);
  const videoRef = useRef();
  const controlsRef = useRef(null);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    return () => { try { controlsRef.current?.stop(); } catch {} };
  }, []);

  const savePrefs = (k, c, m) => localStorage.setItem(PREFS_KEY, JSON.stringify({ karat: k, category: c, milyem: m }));

  const uploadPhoto = async (file, setter, setBusy) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setter(data.url);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Fotoğraf yüklenemedi");
    } finally {
      setBusy(false);
    }
  };

  const saveAndNext = async () => {
    if (!imageUrl) { toast.error("Önce fotoğraf ekleyin"); return; }
    if (!karat || !gram || !milyem) { toast.error("Ayar, Gram ve Milyem zorunludur"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/products/quick", {
        image_url: imageUrl, karat, gram: parseFloat(gram), milyem: parseFloat(milyem),
        category, subcategory, name, barcode, tray, group, active: true,
      });
      savePrefs(karat, category, milyem);
      setSession((s) => [{ id: data.id, code: data.code, name: data.name, image_url: data.image_url, gram: data.gram }, ...s].slice(0, 12));
      // keep karat/category/milyem, clear the rest
      setGram(""); setName(""); setBarcode(""); setImageUrl("");
      if (photoRef.current) photoRef.current.value = "";
      toast.success(`Eklendi: ${data.code}`);
      setTimeout(() => gramRef.current?.focus(), 50);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const startScan = async () => {
    setScanOpen(true);
    try {
      const { BrowserMultiFormatReader } = await import("@zxing/browser");
      const reader = new BrowserMultiFormatReader();
      const controls = await reader.decodeFromVideoDevice(undefined, videoRef.current, (result) => {
        if (result) {
          setBarcode(result.getText());
          toast.success("Barkod okundu");
          stopScan();
        }
      });
      controlsRef.current = controls;
    } catch (err) {
      toast.error("Kamera açılamadı. Barkodu elle girebilir veya okuyucu ile taratabilirsiniz.");
      setScanOpen(false);
    }
  };

  const stopScan = () => {
    try { controlsRef.current?.stop(); } catch {}
    controlsRef.current = null;
    setScanOpen(false);
  };

  const inputCls = "bg-obsidian border-border text-foreground focus-visible:ring-gold";

  return (
    <div className="max-w-3xl">
      <div className="flex items-center gap-2 mb-1">
        <Zap className="text-gold" size={22} />
        <h1 className="font-display text-3xl text-foreground">Hızlı Ekle</h1>
      </div>
      <p className="text-sm text-muted-foreground mb-6">Mağazada ürünleri seri şekilde ekleyin. Kod otomatik <b>SKU</b> olarak üretilir. Çok sayıda fotoğraf için <b>Toplu Fotoğraf</b> sayfasını kullanın.</p>

      <div className="mt-1">
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <input ref={photoRef} type="file" accept="image/*" capture="environment" className="hidden" data-testid="quick-photo-input" onChange={(e) => uploadPhoto(e.target.files[0], setImageUrl, setUploading)} />
              <button data-testid="quick-photo-button" onClick={() => photoRef.current?.click()} className="w-full aspect-square rounded-lg border-2 border-dashed border-border bg-elevated flex items-center justify-center overflow-hidden hover:border-gold transition-colors">
                {uploading ? (
                  <Loader2 className="animate-spin text-gold" size={28} />
                ) : imageUrl ? (
                  <img src={imgUrl(imageUrl)} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="flex flex-col items-center gap-2 text-muted-foreground">
                    <Camera size={30} /><span className="text-sm">Fotoğraf çek / seç</span>
                  </div>
                )}
              </button>
            </div>

            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] text-muted-foreground mb-1 block">Ayar *</label>
                  <Select value={karat} onValueChange={setKarat}>
                    <SelectTrigger data-testid="quick-karat" className={`${inputCls} h-10`}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-surface border-border text-foreground">
                      {KARATS.map((k) => <SelectItem key={k} value={k}>{k} Ayar</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-[11px] text-muted-foreground mb-1 block">Gram *</label>
                  <Input ref={gramRef} data-testid="quick-gram" type="number" inputMode="decimal" step="0.01" value={gram} onChange={(e) => setGram(e.target.value)} className={`${inputCls} h-10`} autoFocus />
                </div>
                <div>
                  <label className="text-[11px] text-muted-foreground mb-1 block">Milyem *</label>
                  <Input data-testid="quick-milyem" type="number" inputMode="numeric" value={milyem} onChange={(e) => setMilyem(e.target.value)} className={`${inputCls} h-10`} />
                </div>
                <div>
                  <label className="text-[11px] text-muted-foreground mb-1 block">Kategori</label>
                  <Select value={category || "none"} onValueChange={(v) => setCategory(v === "none" ? "" : v)}>
                    <SelectTrigger data-testid="quick-category" className={`${inputCls} h-10`}><SelectValue placeholder="Seç" /></SelectTrigger>
                    <SelectContent className="bg-surface border-border text-foreground max-h-64">
                      <SelectItem value="none">—</SelectItem>
                      {categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <label className="text-[11px] text-muted-foreground mb-1 block">Alt Kategori — opsiyonel</label>
                <SubcategoryInput categories={categories} category={category} value={subcategory} onChange={setSubcategory} className={`${inputCls} h-10`} testid="quick-subcategory" />
              </div>

              <div>
                <label className="text-[11px] text-muted-foreground mb-1 block">Barkod (Pusula) — opsiyonel</label>
                <div className="flex gap-2">
                  <Input data-testid="quick-barcode" value={barcode} onChange={(e) => setBarcode(e.target.value)} placeholder="Okuyucu ile taratın veya girin" className={`${inputCls} h-10`} />
                  <button data-testid="scan-barcode-button" onClick={startScan} className="shrink-0 px-3 h-10 border border-border rounded-md hover:border-gold transition-colors flex items-center gap-1 text-sm"><ScanLine size={16} /> Tara</button>
                </div>
              </div>

              <div>
                <label className="text-[11px] text-muted-foreground mb-1 block">Tabla (Konum) — opsiyonel</label>
                <TrayInput value={tray} onChange={(v) => { setTray(v); saveTray(v); }} className={`${inputCls} h-10`} testid="quick-tray" />
              </div>

              <div>
                <label className="text-[11px] text-muted-foreground mb-1 block">Grup</label>
                <Select value={group || "none"} onValueChange={(v) => setGroup(v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="quick-group" className={`${inputCls} h-10`}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-surface border-border text-foreground">
                    <SelectItem value="none">Genel</SelectItem>
                    <SelectItem value="6007">6007 (yalnızca admin)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-[11px] text-muted-foreground mb-1 block">Ürün Adı — opsiyonel (boşsa otomatik)</label>
                <Input data-testid="quick-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Örn. Baget Yüzük" className={`${inputCls} h-10`} />
              </div>

              <button data-testid="quick-save-button" onClick={saveAndNext} disabled={saving} className="w-full h-12 bg-gold text-obsidian font-semibold rounded-md hover:bg-gold-hover transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                {saving ? <Loader2 className="animate-spin" size={18} /> : <Check size={18} />} Kaydet ve Sıradaki
              </button>
            </div>
          </div>

          {session.length > 0 && (
            <div className="mt-6">
              <div className="text-[10px] tracking-luxe uppercase text-gold mb-2">Bu oturumda eklenen: {session.length}</div>
              <div className="flex gap-2 overflow-x-auto pb-2">
                {session.map((p) => (
                  <div key={p.id} className="shrink-0 w-24" data-testid={`session-item-${p.code}`}>
                    <div className="w-24 h-28 rounded overflow-hidden bg-elevated border border-border">
                      {p.image_url && <img src={imgUrl(p.image_url)} alt="" className="w-full h-full object-cover" />}
                    </div>
                    <div className="font-mono text-[10px] text-gold mt-1 truncate">{p.code}</div>
                    <div className="text-[10px] text-muted-foreground">{fmtGram(p.gram)} gr</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

      {scanOpen && (
        <div className="fixed inset-0 z-50 bg-black/90 flex flex-col items-center justify-center p-4" data-testid="scanner-modal">
          <div className="w-full max-w-sm bg-surface border border-border rounded-lg overflow-hidden">
            <div className="flex items-center justify-between px-4 h-12 border-b border-border">
              <span className="text-sm text-foreground">Barkod Tara</span>
              <button onClick={stopScan} className="text-muted-foreground hover:text-foreground"><X size={18} /></button>
            </div>
            <video ref={videoRef} className="w-full aspect-square object-cover bg-black" muted playsInline />
            <div className="p-3 text-center text-[11px] text-muted-foreground">Barkodu kameraya gösterin</div>
          </div>
        </div>
      )}
    </div>
  );
}
